import { Utensils } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { formatMinor } from '@/routes/disruptions/care-desk/care-desk-model';
import { FieldLabel } from './airport-ui';

// The Meals & card tab's side card: what the passenger is OWED (the policy
// entitlement, what triggered it, lounge, settled top-ups, a care outcome
// declared outside the wallet) — beside the plastic that delivers it. The card
// panel says what is on the card; this says why. Presentational.

function Field({ label, value, sub }: { label: string; value: string; sub?: string | undefined }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <FieldLabel>{label}</FieldLabel>
      <span className="text-sm font-semibold tabular-nums">{value}</span>
      {sub ? <span className="text-xs text-muted-foreground">{sub}</span> : null}
    </div>
  );
}

export function EntitlementCard({ group, hotelInPlay }: { group: DisruptionPnrGroupRow; hotelInPlay: boolean }) {
  const { t } = useTranslation('pnr');
  const e = group.mealVoucher;
  const currency = e?.currency || 'USD';
  const allowance = typeof e?.amountUsd === 'number' ? formatMinor(Math.round(e.amountUsd * 100), currency) : undefined;
  const trigger =
    group.accommodationKind === 'MEAL_VOUCHER'
      ? t('entitlement.triggerVoucher')
      : hotelInPlay
        ? t('entitlement.triggerHotel')
        : t('entitlement.triggerNone');

  return (
    <section className="rounded-lg border border-border p-4" data-testid="entitlement-card">
      <FieldLabel className="flex items-center gap-1.5">
        <Utensils className="h-3.5 w-3.5" aria-hidden />
        {t('entitlement.title')}
      </FieldLabel>
      <div className="mt-3 flex flex-col gap-3.5">
        <Field
          label={t('entitlement.allowance')}
          value={allowance ?? t('entitlement.allowanceNone')}
          sub={allowance ? t('entitlement.policySource') : t('entitlement.allowanceNoneHint')}
        />
        <Field label={t('entitlement.trigger')} value={trigger} />
        <Field label={t('entitlement.lounge')} value={e?.includesLounge ? t('entitlement.loungeIncluded') : t('entitlement.loungeNotIncluded')} />
        <Field label={t('entitlement.topUps')} value={formatMinor(e?.toppedUpMinor ?? 0, currency)} />
        <Field
          label={t('entitlement.careOutcome')}
          value={group.careOutcome ? String(group.careOutcome.outcome ?? '—') : t('entitlement.careOutcomeNone')}
          sub={group.careOutcome ? undefined : t('entitlement.careOutcomeNoneHint')}
        />
      </div>
      <p className="mt-4 border-t border-border pt-3 text-[13px] text-muted-foreground">{t('entitlement.footnote')}</p>
    </section>
  );
}
